'use client';

import { useMemo } from 'react';
import type { MatchedGame } from '@rfqsports/shared';
import { useStore } from '@/lib/store';
import { groupGameMarkets, type PricedRow } from '@/lib/odds';
import { cn } from '@/lib/cn';
import { PriceCell } from './price-cell';

interface Props {
  game: MatchedGame;
}

interface RowCells {
  moneyline: PricedRow | null;
  spread: PricedRow | null;
  total: PricedRow | null;
}

/**
 * Polymarket-style row card: top strip shows the kickoff time + venue chips
 * + Game View deeplink; below it are the two team rows with moneyline /
 * spread / total cells aligned in three columns. The whole card is one
 * clickable link to `/game/[id]` — the inline cells are render-only on the
 * lobby (clicking them just navigates into the game page, where the trade
 * ticket can load each outcome).
 */
export function GameRow({ game }: Props): JSX.Element {
  const books = useStore((s) => s.books);
  const groups = useMemo(() => groupGameMarkets(game, books), [game, books]);
  const { moneyline, spread, total } = useMemo<RowCells>(() => {
    const ml = groups.find((g) => g.marketType === 'moneyline')?.rows ?? [];
    // Surface the *first* available spread / total bucket — the per-game page
    // shows every line.
    const sp = groups.find((g) => g.marketType === 'spread')?.rows ?? [];
    const tot = groups.find((g) => g.marketType === 'total')?.rows ?? [];
    return {
      moneyline: ml[0] ?? null,
      spread: sp[0] ?? null,
      total: tot[0] ?? null,
    };
  }, [groups]);

  const startLabel = startTime(game.startTs);
  const homeMl = pickByLabel(moneyline, game.homeTeam);
  const awayMl = pickByLabel(moneyline, game.awayTeam);
  const homeSpread = spread?.label.includes(game.homeTeam) ? spread : findOther(spread, game.homeTeam);
  const awaySpread = spread?.label.includes(game.awayTeam) ? spread : findOther(spread, game.awayTeam);
  const overTotal = total?.label.toLowerCase().startsWith('over') ? total : null;
  const underTotal = total?.label.toLowerCase().startsWith('under') ? total : null;

  return (
    <a
      href={`/game/${encodeURIComponent(game.id)}`}
      className={cn(
        'block rounded-xl border border-border bg-panel transition-colors',
        'hover:border-border/80 hover:bg-panel/80',
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-mono text-text">{startLabel}</span>
          <VenueChips game={game} />
        </div>
        <span className="rounded-full bg-panel-2 px-3 py-1 text-xs font-semibold text-text">
          Game View →
        </span>
      </header>

      <div className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-2 px-4 py-3 text-sm">
        <ColumnLabels />

        <TeamLine
          team={game.awayTeam}
          moneylineRow={awayMl ? { label: shortPriceLabel(game.awayTeam), price: bestPrice(awayMl) } : null}
          spreadLabel={spreadLabel(spread, 'away', game.homeTeam, game.awayTeam)}
          spreadPrice={awaySpread ? bestPrice(awaySpread) : null}
          totalLabel={totalLabel(overTotal, underTotal, 'over')}
          totalPrice={overTotal ? bestPrice(overTotal) : null}
        />
        <TeamLine
          team={game.homeTeam}
          moneylineRow={homeMl ? { label: shortPriceLabel(game.homeTeam), price: bestPrice(homeMl) } : null}
          spreadLabel={spreadLabel(spread, 'home', game.homeTeam, game.awayTeam)}
          spreadPrice={homeSpread ? bestPrice(homeSpread) : null}
          totalLabel={totalLabel(overTotal, underTotal, 'under')}
          totalPrice={underTotal ? bestPrice(underTotal) : null}
        />
      </div>
    </a>
  );
}

function ColumnLabels(): JSX.Element {
  return (
    <>
      <span className="self-end text-[10px] uppercase tracking-wider text-muted" />
      <span className="self-end text-[10px] uppercase tracking-wider text-muted text-center">
        Moneyline
      </span>
      <span className="self-end text-[10px] uppercase tracking-wider text-muted text-center">
        Spread
      </span>
      <span className="self-end text-[10px] uppercase tracking-wider text-muted text-center">
        Total
      </span>
    </>
  );
}

function TeamLine({
  team,
  moneylineRow,
  spreadLabel,
  spreadPrice,
  totalLabel,
  totalPrice,
}: {
  team: string;
  moneylineRow: { label: string; price: number | null } | null;
  spreadLabel: string;
  spreadPrice: number | null;
  totalLabel: string;
  totalPrice: number | null;
}): JSX.Element {
  return (
    <>
      <div className="flex items-center gap-3">
        <TeamLogo team={team} />
        <span className="text-sm font-semibold text-text">{team}</span>
      </div>
      <div className="flex justify-center">
        <PriceCell
          static
          label={moneylineRow?.label ?? team.slice(0, 3).toUpperCase()}
          price={moneylineRow?.price ?? null}
        />
      </div>
      <div className="flex justify-center">
        <PriceCell static label={spreadLabel} price={spreadPrice} />
      </div>
      <div className="flex justify-center">
        <PriceCell static label={totalLabel} price={totalPrice} />
      </div>
    </>
  );
}

function TeamLogo({ team }: { team: string }): JSX.Element {
  const initials = team
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 3)
    .join('')
    .toUpperCase();
  return (
    <span
      className="grid h-8 w-8 place-items-center rounded-md bg-accent-strong/15 text-[11px] font-bold text-accent-strong"
      aria-hidden
    >
      {initials || '?'}
    </span>
  );
}

function VenueChips({ game }: { game: MatchedGame }): JSX.Element {
  return (
    <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted">
      {game.polymarket ? <Chip label="PM" tone="ok" /> : null}
      {game.kalshi ? <Chip label="KA" tone="ok" /> : null}
      {!game.polymarket && game.kalshi ? <Chip label="single" tone="muted" /> : null}
      {game.polymarket && !game.kalshi ? <Chip label="single" tone="muted" /> : null}
    </span>
  );
}

function Chip({ label, tone }: { label: string; tone: 'ok' | 'muted' }): JSX.Element {
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        tone === 'ok' ? 'bg-ok/15 text-ok' : 'bg-panel-2 text-muted',
      )}
    >
      {label}
    </span>
  );
}

function startTime(ts: number): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(ts));
}

function pickByLabel(row: PricedRow | null, label: string): PricedRow | null {
  if (!row) return null;
  return row.label === label ? row : null;
}

function shortPriceLabel(team: string): string {
  return team
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 3)
    .join('')
    .toUpperCase();
}

function spreadLabel(
  row: PricedRow | null,
  side: 'home' | 'away',
  home: string,
  away: string,
): string {
  if (!row) return side === 'home' ? home.slice(0, 3).toUpperCase() : away.slice(0, 3).toUpperCase();
  // groupGameMarkets returns "Team +N" / "Team -N" labels — show the spread number
  // after the team initials so the lobby cell stays compact.
  const match = row.label.match(/([+\-]\d+(\.\d+)?)/);
  const number = match ? ` ${match[1]}` : '';
  const team = side === 'home' ? home : away;
  return `${team.slice(0, 3).toUpperCase()}${number}`;
}

function totalLabel(
  over: PricedRow | null,
  under: PricedRow | null,
  which: 'over' | 'under',
): string {
  const row = which === 'over' ? over : under;
  if (!row) return which === 'over' ? 'O' : 'U';
  const match = row.label.match(/(\d+(\.\d+)?)/);
  return `${which === 'over' ? 'O' : 'U'} ${match?.[1] ?? ''}`.trim();
}

function findOther(_row: PricedRow | null, _team: string): PricedRow | null {
  // The matcher only emits one row per line per side, so the lookup above
  // either finds it or there isn't a paired row to fall back to. This stub
  // exists so the caller can keep its tidy bestPrice() pipeline.
  return null;
}

function bestPrice(row: PricedRow): number | null {
  // The lobby cards prefer ask (the cost-to-buy headline price). Falls back
  // to bid when ask is missing on both venues.
  const pmAsk = row.polymarket?.ask ?? null;
  const kAsk = row.kalshi?.ask ?? null;
  if (pmAsk != null && kAsk != null) return Math.min(pmAsk, kAsk);
  if (pmAsk != null) return pmAsk;
  if (kAsk != null) return kAsk;
  const pmBid = row.polymarket?.bid ?? null;
  const kBid = row.kalshi?.bid ?? null;
  if (pmBid != null && kBid != null) return Math.max(pmBid, kBid);
  return pmBid ?? kBid;
}
